import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  User, 
  Mail, 
  Calendar, 
  Shield, 
  Crown,
  Settings
} from 'lucide-react';

export default function Profile() {
  const { user } = useAuth();

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="profile-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center">
              <Settings className="w-6 h-6 text-[#00FF94]" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-3xl text-white">
                Profile Settings
              </h1>
              <p className="text-gray-500">
                Manage your account information
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-6">
          {/* Profile Card */}
          <Card className="bg-[#0a0a0a] border-[#262626]" data-testid="profile-info-card">
            <CardHeader>
              <CardTitle className="text-white">Account Information</CardTitle>
              <CardDescription className="text-gray-500">Your personal details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-6">
                {/* Avatar */}
                <div className="w-20 h-20 rounded-xl bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center">
                  <User className="w-10 h-10 text-[#00FF94]" />
                </div>

                {/* Info */}
                <div className="flex-1 space-y-4">
                  <div className="flex items-center gap-3">
                    <h2 className="font-heading font-semibold text-xl text-white">{user?.name}</h2>
                    {user?.is_premium && (
                      <Badge className="bg-[#00FF94]/10 text-[#00FF94] border-[#00FF94]/20">
                        <Crown className="w-3 h-3 mr-1" />
                        Premium
                      </Badge>
                    )}
                  </div>

                  <div className="grid gap-3">
                    <div className="flex items-center gap-3 text-gray-400">
                      <Mail className="w-4 h-4 text-gray-500" />
                      <span className="text-sm">{user?.email}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-400">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <span className="text-sm">Member since {formatDate(user?.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Privacy Score Card */}
          <Card className="bg-[#0a0a0a] border-[#262626]" data-testid="profile-score-card">
            <CardHeader>
              <CardTitle className="text-white">Privacy Score</CardTitle>
              <CardDescription className="text-gray-500">Your current protection level</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 rounded-xl bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center">
                  <Shield className="w-8 h-8 text-[#00FF94]" />
                </div>
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono font-bold text-4xl text-white">{user?.privacy_score || 0}</span>
                    <span className="text-gray-500">/100</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Visit your dashboard to improve your score
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subscription Card */}
          <Card className="bg-[#0a0a0a] border-[#262626]" data-testid="profile-subscription-card">
            <CardHeader>
              <CardTitle className="text-white">Subscription</CardTitle>
              <CardDescription className="text-gray-500">Your current plan</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-[#171717] rounded-lg border border-[#262626]">
                <div>
                  <h3 className="font-medium text-white mb-1">
                    {user?.is_premium ? 'Premium Plan' : 'Free Plan'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {user?.is_premium 
                      ? 'Full access to all features' 
                      : 'Basic privacy protection features'
                    }
                  </p>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-mono ${user?.is_premium ? 'bg-[#00FF94]/10 text-[#00FF94] border border-[#00FF94]/20' : 'bg-[#262626] text-gray-400'}`}>
                  {user?.is_premium ? 'Active' : 'Free'}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Data Privacy Note */}
          <div className="bg-[#0a0a0a] border border-[#262626] rounded-xl p-6">
            <h3 className="font-medium text-white mb-2">Your Data Privacy</h3>
            <p className="text-sm text-gray-500 leading-relaxed">
              We store minimal data needed to provide our service. Your privacy score and settings 
              are stored locally when possible. We never sell your data to third parties or share 
              it with advertisers. You can request deletion of your account at any time.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
